// services/gtmTracking.ts - Version simple et fonctionnelle
//console.log('🔧 GTM Service Loading...');

export interface GTMEvent {
    event: string;
    [key: string]: any;
}

export interface UserData {
    user_id?: string;
    member_number?: string;
    first_name?: string;
    last_name?: string;
    city?: string;
    province?: string;
    country?: string;
    referral_code?: string;
}

// Fonction utilitaire pour vérifier GTM
const isGTMAvailable = (): boolean => {
    const available = typeof window !== 'undefined' &&
        (window as any).dataLayer &&
        Array.isArray((window as any).dataLayer);

    if (!available) {
    //    console.log('⚠️ GTM DataLayer not available, events will be logged instead');
    }
    return available;
};

// Fonction générique pour envoyer des événements
const pushToGTM = (eventData: GTMEvent): void => {
 //   console.log('📊 GTM Event:', eventData);

    if (isGTMAvailable()) {
        try {
            (window as any).dataLayer.push(eventData);
        //    console.log('✅ Event sent to GTM DataLayer');
        } catch (error) {
            console.error('❌ GTM Error:', error);
        }
    } else {
    //    console.log('📝 Event logged (GTM not available):', eventData);
    }
};

// ==========================================
// ÉVÉNEMENTS D'ADHÉSION
// ==========================================

export const trackAdhesionStart = (referral_code?: string): void => {
    pushToGTM({
        event: 'udb_adhesion_start',
        has_referral: !!referral_code,
        referral_code: referral_code || null,
        timestamp: new Date().toISOString()
    });
};

export const trackAdhesionStep = (step_number: number, step_name: string, action: 'start' | 'complete' | 'error'): void => {
    pushToGTM({
        event: 'udb_adhesion_step',
        step_number,
        step_name,
        action,
        timestamp: new Date().toISOString()
    });
};

export const trackValidationError = (step: string, field: string, error_type: string): void => {
    pushToGTM({
        event: 'udb_validation_error',
        step,
        field,
        error_type,
        timestamp: new Date().toISOString()
    });
};

export const trackNipVerification = (nip_format_valid: boolean, nip_available: boolean): void => {
    pushToGTM({
        event: 'udb_nip_verification',
        nip_format_valid,
        nip_available,
        timestamp: new Date().toISOString()
    });
};

export const trackReferralValidation = (code: string, valid: boolean, referrer_info?: any): void => {
    pushToGTM({
        event: 'udb_referral_validation',
        referral_code: code,
        valid,
        referrer_city: referrer_info?.city || null,
        referrer_province: referrer_info?.province || null,
        timestamp: new Date().toISOString()
    });
};

export const trackAdhesionSuccess = (userData: UserData): void => {
    pushToGTM({
        event: 'udb_adhesion_success',
        user_id: userData.member_number,
        member_number: userData.member_number,
        member_name: `${userData.first_name} ${userData.last_name}`,
        member_city: userData.city,
        member_province: userData.province,
        member_country: userData.country || 'Gabon',
        has_referral: !!userData.referral_code,
        referral_code: userData.referral_code || null,
        timestamp: new Date().toISOString(),
        value: 1,
        currency: 'XAF'
    });
};

export const trackAdhesionConversion = (userData: UserData): void => {
    pushToGTM({
        event: 'purchase',
        transaction_id: userData.member_number,
        value: 1,
        currency: 'XAF',
        items: [{
            item_id: 'udb_membership',
            item_name: 'Adhésion UDB',
            category: 'Membership',
            quantity: 1,
            price: 1
        }],
        user_id: userData.member_number,
        custom_parameters: {
            member_city: userData.city,
            member_province: userData.province,
            has_referral: !!userData.referral_code
        }
    });
};

export const trackAdhesionFunnelStart = (): void => {
    pushToGTM({
        event: 'begin_checkout',
        currency: 'XAF',
        value: 1,
        items: [{
            item_id: 'udb_membership',
            item_name: 'Adhésion UDB',
            category: 'Membership',
            quantity: 1,
            price: 1
        }]
    });
};

// ==========================================
// ÉVÉNEMENTS DE NAVIGATION
// ==========================================

export const trackAdhererClick = (source: 'header' | 'hero' | 'footer' | 'menu_mobile'): void => {
    pushToGTM({
        event: 'udb_adherer_click',
        click_source: source,
        button_text: 'ADHÉRER',
        timestamp: new Date().toISOString()
    });
};

export const trackEspaceMembreClick = (source: 'header' | 'footer' | 'menu_mobile'): void => {
    pushToGTM({
        event: 'udb_espace_membre_click',
        click_source: source,
        button_text: 'ESPACE MEMBRE',
        timestamp: new Date().toISOString()
    });
};

export const trackMenuClick = (menu_item: string, destination: string): void => {
    pushToGTM({
        event: 'udb_menu_navigation',
        menu_item,
        destination,
        timestamp: new Date().toISOString()
    });
};

export const trackClick = (element: string, context?: string): void => {
    pushToGTM({
        event: 'udb_click',
        element,
        context: context || 'unknown',
        timestamp: new Date().toISOString()
    });
};

// ==========================================
// ÉVÉNEMENTS ESPACE MEMBRE
// ==========================================

export const trackMemberLogin = (member_number: string, member_name: string): void => {
    pushToGTM({
        event: 'udb_member_login',
        user_id: member_number,
        member_number,
        member_name,
        timestamp: new Date().toISOString()
    });
};

export const trackMemberLoginFailed = (reason: 'invalid_credentials' | 'member_not_found'): void => {
    pushToGTM({
        event: 'udb_member_login_failed',
        failure_reason: reason,
        timestamp: new Date().toISOString()
    });
};

export const trackMemberCardDownload = (member_number: string, format: 'image' | 'pdf'): void => {
    pushToGTM({
        event: 'udb_member_card_download',
        user_id: member_number,
        download_format: format,
        timestamp: new Date().toISOString()
    });
};

export const trackAdhesionPDFGeneration = (member_number: string, success: boolean): void => {
    pushToGTM({
        event: 'udb_adhesion_pdf_generation',
        user_id: member_number,
        success,
        timestamp: new Date().toISOString()
    });
};

// ==========================================
// ÉVÉNEMENTS DE PARTAGE SOCIAL
// ==========================================

export const trackSocialShare = (platform: string, member_number?: string, referral_code?: string): void => {
    pushToGTM({
        event: 'udb_social_share',
        platform,
        user_id: member_number || null,
        referral_code: referral_code || null,
        timestamp: new Date().toISOString()
    });
};

export const trackReferralLinkCopy = (member_number: string, referral_code: string): void => {
    pushToGTM({
        event: 'udb_referral_link_copy',
        user_id: member_number,
        referral_code,
        timestamp: new Date().toISOString()
    });
};

// ==========================================
// ÉVÉNEMENTS D'INTERACTION
// ==========================================

export const trackQRCodeView = (member_number?: string): void => {
    pushToGTM({
        event: 'udb_qr_code_view',
        user_id: member_number || null,
        timestamp: new Date().toISOString()
    });
};

export const trackQRCodeDownload = (member_number?: string): void => {
    pushToGTM({
        event: 'udb_qr_code_download',
        user_id: member_number || null,
        timestamp: new Date().toISOString()
    });
};

export const trackSectionView = (section_name: string): void => {
    pushToGTM({
        event: 'udb_section_view',
        section_name,
        timestamp: new Date().toISOString()
    });
};

// ==========================================
// ÉVÉNEMENTS GÉNÉRAUX
// ==========================================

export const trackPageLoadTime = (page: string, load_time_ms: number): void => {
    pushToGTM({
        event: 'udb_page_performance',
        page,
        load_time_ms,
        timestamp: new Date().toISOString()
    });
};

export const trackTechnicalError = (error_type: string, error_message: string, context?: string): void => {
    pushToGTM({
        event: 'udb_technical_error',
        error_type,
        error_message,
        context: context || 'unknown',
        timestamp: new Date().toISOString()
    });
};

export const trackPageView = (page_title: string, page_location: string): void => {
    pushToGTM({
        event: 'page_view',
        page_title,
        page_location,
        timestamp: new Date().toISOString()
    });
};

export const trackStepTime = (step_name: string, time_spent_seconds: number): void => {
    pushToGTM({
        event: 'udb_step_timing',
        step_name,
        time_spent_seconds,
        timestamp: new Date().toISOString()
    });
};

// Fonction de test
export const testGTMService = (): void => {
 //   console.log('🧪 Testing GTM Service...');
    pushToGTM({
        event: 'udb_test_event',
        test: true,
        timestamp: new Date().toISOString()
    });
 //   console.log('✅ GTM Service test completed');
};

// Export par défaut pour compatibilité
const GTMTrackingService = {
    trackAdhesionStart,
    trackAdhesionStep,
    trackValidationError,
    trackNipVerification,
    trackReferralValidation,
    trackAdhesionSuccess,
    trackAdhesionConversion,
    trackAdhesionFunnelStart,
    trackAdhererClick,
    trackEspaceMembreClick,
    trackMenuClick,
    trackClick,
    trackMemberLogin,
    trackMemberLoginFailed,
    trackMemberCardDownload,
    trackAdhesionPDFGeneration,
    trackSocialShare,
    trackReferralLinkCopy,
    trackQRCodeView,
    trackQRCodeDownload,
    trackSectionView,
    trackPageLoadTime,
    trackTechnicalError,
    trackPageView,
    trackStepTime,
    testGTMService
};

export default GTMTrackingService;

//console.log('✅ GTM Service Loaded Successfully');
